import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { checkoutErrors, CheckoutStatus, Features, PaymentMethodType, TerminalErrorCode, TopazStatus } from '../../model'
import { getPrefetchRoutes, Route, usePostLoginRoute } from '../../config/router'
import { Cohort } from '@afterpaytouch/portal-api'
import {
  selectPurchaseMethod,
  setCheckoutStatus,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useCheckoutError,
  useCohort,
  useConsumerFacilityCohort,
  useConsumerSelector,
  useCountry,
  useCreditFacilityRedirect,
  useEffectiveRoutingDecision,
  usePreferredPaymentMethod,
  useShouldSetApplePayAsPurchaseMethod,
  usePreferredBankAccount,
  usePreferredCard,
  useRecoverableTerminalErrorsState,
  useRoutingDecision,
  useRoutingDecisionReason,
  useShouldShowCashAppTooltip,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useShouldUseBankAccount,
  useIsBankAccountSelectionFailed,
  useTopazState,
  useSavedCards,
  handlePreferredFallback,
  updatePreferredCardWithError,
  updateSavedCardsWithError,
  useStartCheckout,
  setIsCreditCardDisabled,
  setCheckoutError,
  useIsAUPayMonthlyOrderLimiteTerminal,
  setLoggedInLoadingMessageKey,
  useActiveCashAppPay,
  useOfferDetailPBI,
  useIsEligibleForAddPaymentMethodFlowExperiment,
  useIsControlForAddPaymentMethodFlowExperiment,
  useAddPaymentMethodFlowExperimentTrigger,
  useIsInlinePaymentOptions,
  useHoldRoutingForLdReady,
  useShouldShowWelcomeWithInitialLimit,
  accountLookup,
  useRequiresAccountRefresh,
  setRequiresAccountRefresh,
  useReadyFeatureFlag,
  useExperiment,
} from '../../state'
import { creditCheckService } from '@afterpaytouch/portal-api/consumer/creditCheck'
import { Country } from '@afterpaytouch/portal-api/types'
import { consumeUsFebConsentInteractiveLogin } from '../../flow/one-offer-page/consentLoginSignal'
import { consumeUsFebConsentCopyDisplayed } from '../../flow/one-offer-page/consentCopySignal'
import { phoneNumberIsCleared } from '../../../src/utils/phone-numbers'
import { BankAccountDetails, RecoverableTerminalErrorStatus } from '@afterpay/types'
import { Text, useToast } from '@afterpaytouch/core'
import { useFlag } from '@afterpaytouch/feature'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { useTranslation } from '../../utils/convergence'

const US_FEB_CONSENT_POST_TIMEOUT_MS = 5000

export const useTrackOneOfferPageRouteResolved = (): void => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { status } = useCheckoutState()
  const routingDecision = useRoutingDecision()
  const routingDecisionReason = useRoutingDecisionReason()
  const effectiveRoutingDecision = useEffectiveRoutingDecision()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const experiment = useExperiment('OneOfferPage', false)
  const hasTracked = useRef(status === CheckoutStatus.Started)

  useEffect(() => {
    if (hasTracked.current || status !== CheckoutStatus.Started || !hasFeatureFlagsReady) {
      return
    }
    hasTracked.current = true
    logEvent(TrackingEvent.ONE_OFFER_PAGE_ROUTE_RESOLVED, {
      routingDecision: routingDecision ?? 'ABSENT',
      routingDecisionReason: routingDecisionReason ?? 'NONE',
      effectiveRoutingDecision: effectiveRoutingDecision ?? 'LEGACY_FALLBACK',
      experimentFlag: experiment.active ? `${experiment.name}${experiment.value}` : 'INACTIVE',
    })
  }, [status, routingDecision, routingDecisionReason, effectiveRoutingDecision, hasFeatureFlagsReady, experiment, logEvent])
}

export interface PostLoginNavigation {
  navigatePostLogin: () => void
}

export const usePostLoginNavigation = (): PostLoginNavigation => {
  const router = useAppRouter()
  const postLoginRoute = usePostLoginRoute()
  const consumerFacilityCohort = useConsumerFacilityCohort()
  const redirectToCreditFacility = useCreditFacilityRedirect()
  const isExistingLcccUser =
    consumerFacilityCohort?.getStartedContext === 'LCCC_EXISTING' &&
    consumerFacilityCohort?.eligibility === 'eligible' &&
    !consumerFacilityCohort?.enteredAsNewUser
  const navigatePostLogin = useCallback(() => {
    if (isExistingLcccUser && postLoginRoute === Route.GET_STARTED) {
      redirectToCreditFacility()
    } else {
      router.replace(postLoginRoute)
    }
  }, [isExistingLcccUser, redirectToCreditFacility, router, postLoginRoute])

  return { navigatePostLogin }
}

export const useSyncApplePayFlagsToStore = (): void => {
  const dispatch = useAppDispatch()
  const isApplePayCreditCardDisabled = useFlag<boolean>(Features.APPLE_PAY_DISABLE_CREDIT_CARD_IN_CHECKOUT, false)

  useEffect(() => {
    dispatch(setIsCreditCardDisabled(isApplePayCreditCardDisabled))
  }, [isApplePayCreditCardDisabled])
}

export const useStartCheckoutOnceReady = (): boolean => {
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { status } = useCheckoutState()
  const { mobile, identityCheckStatus } = useConsumerSelector()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { status: recoverableFlowStatus } = useRecoverableTerminalErrorsState()
  const requiresAccountRefresh = useRequiresAccountRefresh()
  const { doStartCheckoutAndHandleError } = useStartCheckout()
  const [startRequestCompleted, setStartRequestCompleted] = useState(false)
  const startSequenceInFlight = useRef(false)
  const country = useCountry()
  const isEvergreenConsentEnabled = useReadyFeatureFlag<boolean>(Features.ENABLE_CHECKOUT_EVERGREEN_CONSENT, false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const postUsFebConsentIfApplicable = async (): Promise<void> => {
    if (!isEvergreenConsentEnabled || country !== Country.US || !consumeUsFebConsentInteractiveLogin() || !consumeUsFebConsentCopyDisplayed()) {
      return
    }
    const consentMutation = dispatch(
      creditCheckService.endpoints.updateConsentStatus.initiate({
        consentStatus: { creditChecks: { consented: true, version: 'V4' } },
        channel: 'CHECKOUT_LOGIN',
      })
    )
    const consentRequest = consentMutation.unwrap().then(
      () => 'posted' as const,
      () => 'failed' as const
    )
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    try {
      const outcome = await Promise.race([
        consentRequest,
        new Promise<'timed-out'>((resolve) => {
          timeoutHandle = setTimeout(resolve, US_FEB_CONSENT_POST_TIMEOUT_MS, 'timed-out')
        }),
      ])
      logEvent(TrackingEvent.ONE_OFFER_PAGE_LOGIN_CONSENT_POSTED, { success: outcome === 'posted', outcome })
    } finally {
      clearTimeout(timeoutHandle)
      consentMutation.reset()
    }
  }

  useEffect(() => {
    const startCheckoutAndHandleError = async (): Promise<void> => {
      if (startSequenceInFlight.current) {
        return
      }
      startSequenceInFlight.current = true
      try {
        if (requiresAccountRefresh) {
          const result = await dispatch(accountLookup())
          if (accountLookup.fulfilled.match(result)) {
            dispatch(setRequiresAccountRefresh(false))
          }
        }
        await postUsFebConsentIfApplicable()
        await doStartCheckoutAndHandleError()
        setLoggedInLoadingMessageKey(null)
        setStartRequestCompleted(true)
      } finally {
        startSequenceInFlight.current = false
      }
    }

    if (phoneNumberIsCleared(mobile)) {
      router.replace(Route.ADD_MOBILE)
    } else if (
      status === CheckoutStatus.Loaded ||
      status === CheckoutStatus.Closed ||
      status === CheckoutStatus.IdentityIneligible ||
      recoverableFlowStatus === RecoverableTerminalErrorStatus.Finished
    ) {
      if (consumerFeatureFlagReady) {
        startCheckoutAndHandleError()
      }
    }
  }, [consumerFeatureFlagReady, recoverableFlowStatus, identityCheckStatus])

  return startRequestCompleted || status === CheckoutStatus.Started
}

export interface PaymentMethodRoutingArgs extends Pick<PostLoginNavigation, 'navigatePostLogin'> {
  requestCompleted: boolean
}

export const usePaymentMethodRouting = ({ requestCompleted, navigatePostLogin }: PaymentMethodRoutingArgs): void => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const toast = useToast()
  const { textSize } = useCheckoutSizes()
  const { status } = useCheckoutState()
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()
  const isBankAccountSelectionFailed = useIsBankAccountSelectionFailed()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const preferredBankAccount = usePreferredBankAccount()
  const preferredCard = usePreferredCard()
  const savedCards = useSavedCards()
  const cashAppPay = useActiveCashAppPay()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const topazStatus = useTopazState()
  const { shouldOverrideCashAppPay } = useShouldShowCashAppTooltip()
  const isEligibleForAddPaymentMethodFlowExperiment = useIsEligibleForAddPaymentMethodFlowExperiment()
  const isControlForAddPaymentMethodFlowExperiment = useIsControlForAddPaymentMethodFlowExperiment()
  const triggerAddPaymentMethodFlowExperiment = useAddPaymentMethodFlowExperimentTrigger()
  const shouldShowWelcomeWithInitialLimit = useShouldShowWelcomeWithInitialLimit()
  const cohort = useCohort()
  const holdRoutingForLdReady = useHoldRoutingForLdReady()
  const isPayMonthlyOrderLimitTerminal = useIsAUPayMonthlyOrderLimiteTerminal()
  const [checkAlternativePreferred, setCheckAlternativePreferred] = useState(false)

  useEffect(() => {
    if (holdRoutingForLdReady) {
      return
    }
    const fallback = savedCards.filter((card) => card.error === null).pop() ?? null
    if (checkAlternativePreferred && fallback != null) {
      dispatch(handlePreferredFallback(fallback, navigatePostLogin))
    } else if (checkAlternativePreferred && fallback === null) {
      dispatch(updatePreferredCardWithError(preferredCard, savedCards))
      navigatePostLogin()
    }
  }, [checkAlternativePreferred, savedCards, holdRoutingForLdReady])

  useEffect(() => {
    if (isPayMonthlyOrderLimitTerminal) {
      return
    }
    if (status === CheckoutStatus.Started && requestCompleted && hasFeatureFlagsReady && !checkAlternativePreferred) {
      if (shouldShowWelcomeWithInitialLimit) {
        router.replace(Route.WELCOME_WITH_INITIAL_LIMIT)
        return
      }

      if (cashAppPay.length > 0 && (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY || shouldOverrideCashAppPay)) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }, false))
      } else if (shouldUseApplePay) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.APPLE_PAY }))
      } else if (shouldUseBankAccount) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.ACH_BANK_ACCOUNT, account: preferredBankAccount?.account as BankAccountDetails }, false))
      } else if (preferredPaymentMethod === PaymentMethodType.CREDIT_CARD && preferredCard?.card !== null) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: preferredCard?.card?.token as string } }))
      } else if (cashAppPay.length > 0) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }, false))
      } else if (preferredCard?.card !== null) {
        dispatch(selectPurchaseMethod({ type: PaymentMethodType.CREDIT_CARD, card: { token: preferredCard?.card?.token as string } }))
      } else if (!isConsumerLendingEligible) {
        if (cohort === Cohort.CashOnly) {
          dispatch(setCheckoutError({ error: TerminalErrorCode.Default }))
          router.replace(Route.TERMINAL_ERROR)
        } else if (isEligibleForAddPaymentMethodFlowExperiment) {
          triggerAddPaymentMethodFlowExperiment()
          navigatePostLogin()
        } else {
          if (isControlForAddPaymentMethodFlowExperiment) {
            triggerAddPaymentMethodFlowExperiment()
          }
          dispatch(setCheckoutStatus(CheckoutStatus.LackOfEligiblePaymentMethod))
          router.replace(Route.ADD_PAYMENT_METHOD)
        }
        return
      }

      if (isBankAccountSelectionFailed) {
        toast({
          message: () => (
            <>
              <Text size={textSize} bold>
                {t('summary:bankAccountErrors:fraudCheckNotAllowed:heading', { lastFour: preferredBankAccount?.account?.numberMask })}
              </Text>
              <Text size={textSize}>{t('summary:bankAccountErrors:fraudCheckNotAllowed:message')}</Text>
            </>
          ),
          kind: 'Default',
          toastTime: 5000,
        })
      }

      if (preferredPaymentMethod !== PaymentMethodType.CREDIT_CARD || isConsumerLendingEligible) {
        navigatePostLogin()
      }
    }
  }, [status, requestCompleted, hasFeatureFlagsReady, shouldShowWelcomeWithInitialLimit, isPayMonthlyOrderLimitTerminal])

  useEffect(() => {
    if (requestCompleted && topazStatus.purchase.status > TopazStatus.Saving && !checkAlternativePreferred) {
      if (topazStatus.purchase.status === TopazStatus.Saved || preferredPaymentMethod !== PaymentMethodType.CREDIT_CARD) {
        if (!holdRoutingForLdReady) {
          navigatePostLogin()
        }
      } else {
        dispatch(updateSavedCardsWithError(topazStatus.purchase.status, preferredCard?.card?.token, savedCards))
        setCheckAlternativePreferred(true)
      }
    }
  }, [topazStatus.purchase.status, requestCompleted, holdRoutingForLdReady])
}

export const usePrefetchPostLoginRoutes = (): void => {
  const router = useAppRouter()
  const country = useCountry()
  const isInlinePaymentOptions = useIsInlinePaymentOptions()
  const verifyRoutes = getPrefetchRoutes(country)

  useEffect(() => {
    router.prefetch(Route.ADD_PAYMENT_METHOD)
    router.prefetch(Route.ADD_MOBILE)
    router.prefetch(Route.TERMINAL_ERROR)
    router.prefetch(Route.SUMMARY)
    if (isInlinePaymentOptions) {
      router.prefetch(Route.PAYMENT_OPTIONS)
    } else {
      router.prefetch(Route.PAYMENT_TYPES)
    }
    router.prefetch(Route.PAY_MONTHLY_ONLY)
    router.prefetch(Route.WELCOME_WITH_INITIAL_LIMIT)
    verifyRoutes.map((url) => router.prefetch(url))
  }, [verifyRoutes, isInlinePaymentOptions])
}

export const usePBIRejectionRedirect = (): void => {
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const offerDetailPBI = useOfferDetailPBI()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const isPayMonthlyOrderLimitTerminal = useIsAUPayMonthlyOrderLimiteTerminal()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()

  useEffect(() => {
    if (!hasFeatureFlagsReady) {
      return
    }
    if (offerDetailPBI?.rejectedReasonCode && !isConsumerLendingEligible && !isPayMonthlyOrderLimitTerminal) {
      dispatch(setCheckoutError({ error: checkoutErrors[offerDetailPBI?.rejectedReasonCode] ?? TerminalErrorCode.Default }))
      router.replace(Route.TERMINAL_ERROR)
    }
  }, [hasFeatureFlagsReady, offerDetailPBI?.rejectedReasonCode, isConsumerLendingEligible, isPayMonthlyOrderLimitTerminal])
}

export const useDispatchAUPayMonthlyOrderLimitTerminalError = (): void => {
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const checkoutError = useCheckoutError()
  const isPayMonthlyOrderLimitTerminal = useIsAUPayMonthlyOrderLimiteTerminal()

  useEffect(() => {
    if (isPayMonthlyOrderLimitTerminal) {
      dispatch(
        setCheckoutError({
          error: TerminalErrorCode.PayMonthlyOrderAmountTooHigh,
          errorCode: checkoutError?.errorCode,
          extra: {
            facilityApplicationEligibility: checkoutError?.extra?.facilityApplicationEligibility,
            maximumOrderValue: checkoutError?.extra?.maximumOrderValue,
          },
        })
      )
      router.replace(Route.TERMINAL_ERROR)
    }
  }, [isPayMonthlyOrderLimitTerminal])
}
